import React, { useEffect, useState } from 'react';
import { rolesService } from '../../services/rolesService';
import { Role, PermissionDef } from '../../types';
import { toast } from 'react-toastify';
import { 
  ActionButton, ActionButtons, Button, ButtonGroup, Form, FormGroup, 
  IconButton, Input, Label, Modal, ModalContent, Table, TableCell, TableHeader, TableWrapper, Select
} from '../shared/StyledComponents';
import { FiPlus, FiTrash2, FiEdit } from 'react-icons/fi';

const Plus = FiPlus as React.ComponentType;
const Trash = FiTrash2 as React.ComponentType;
const Edit = FiEdit as React.ComponentType;

const RolesTab: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PermissionDef[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [name, setName] = useState('');
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [r, p] = await Promise.all([
        rolesService.getRoles(),
        rolesService.getPermissions()
      ]);
      setRoles(r);
      setPermissions(p);
    } catch (e) {
      toast.error('Error cargando roles');
    }
  };

  const handleAdd = () => {
    setEditingRole(null);
    setName('');
    setSelectedPerms([]);
    setShowModal(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setName(role.name);
    setSelectedPerms(role.permissions || []);
    setShowModal(true);
  };

  const togglePerm = (key: string) => {
    setSelectedPerms(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await rolesService.updateRolePermissions(editingRole.id, selectedPerms);
        toast.success('Rol actualizado');
      } else {
        await rolesService.createRole(name, selectedPerms);
        toast.success('Rol creado');
      }
      setShowModal(false);
      await load();
    } catch (e) {
      toast.error('Error guardando rol');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await rolesService.deleteRole(id);
      toast.success('Rol eliminado');
      await load();
    } catch (e) {
      toast.error('Error eliminando rol');
    }
  };

  return (
    <div>
      <ActionButton onClick={handleAdd}><Plus /> Agregar Rol</ActionButton>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Permisos</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <tbody>
            {roles.map(role => (
              <tr key={role.id}>
                <TableCell>{role.name}</TableCell>
                <TableCell>{(role.permissions || []).join(', ') || '-'}</TableCell>
                <TableCell>
                  <ActionButtons>
                    <IconButton variant="edit" onClick={() => handleEdit(role)}><Edit /></IconButton>
                    <IconButton variant="delete" onClick={() => handleDelete(role.id)}><Trash /></IconButton>
                  </ActionButtons>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <Modal show={showModal}>
        <ModalContent>
          <h2>{editingRole ? 'Editar Rol' : 'Agregar Rol'}</h2>
          <Form onSubmit={handleSubmit}>
            {!editingRole && (
              <FormGroup>
                <Label>Nombre</Label>
                <Input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
              </FormGroup>
            )}
            <FormGroup>
              <Label>Permisos</Label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                {permissions.map(p => (
                  <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" checked={selectedPerms.includes(p.key)} onChange={() => togglePerm(p.key)} />
                    <span>{p.name}</span>
                  </label>
                ))}
              </div>
            </FormGroup>
            <ButtonGroup>
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button type="submit" variant="primary">Guardar</Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default RolesTab;